import { FiClock as Clock, FiVideo as Video, FiCalendar as Calendar } from "react-icons/fi";
import { Session } from "../../types/session.entity";
import { Badge } from "src/components/Badge/Badge";
import { displayDate } from "src/utils/dateHandler.util";
import { sessionStateStyles } from "../../utils/session-styles";
import { Link, useLocation } from "react-router-dom";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";

type SessionHeaderProps = {
  session: Session;
};

export const SessionHeader: React.FC<SessionHeaderProps> = ({ session }) => {
  const { search } = useLocation();
  const backToSessions = appendSearchToPath("/sessions", search);

  return (
    <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full -ml-24 -mb-24"></div>

      <div className="relative z-10">
        <Link
          to={backToSessions}
          className="inline-flex items-center rounded-xl bg-white bg-opacity-15 px-4 py-2 text-sm font-medium text-white no-underline transition hover:bg-opacity-25"
        >
          Back to Sessions
        </Link>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Session Details</h1>
          <div className="flex items-center gap-3">
            {session.type === "BOOKING" && session.bookingIds?.length && (
              <Link
                to={`/sessions/${session.bookingIds[0]}`}
                className="px-4 py-2 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl text-sm font-medium hover:bg-opacity-30 transition border border-white border-opacity-30 no-underline"
              >
                Go to Booking
              </Link>
            )}
            <Badge label={session.state} classes={sessionStateStyles[session.state]} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3 bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Video className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-white text-opacity-80 mb-1">Session ID</p>
              <p className="font-mono text-sm font-semibold">{session.id}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-white text-opacity-80 mb-1">Started</p>
              <p className="text-sm font-semibold">{displayDate(session.startTime)}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-white text-opacity-80 mb-1">Duration</p>
              <p className="text-sm font-semibold">{session.totalDurationMins || 0} minutes</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
